import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme, SimpleButton } from '../../../../ui';
import { toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';
import coursioNetworkLogo from '../../../../ui/assets/coursio_network_logo.avif';

const SectionContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  margin-bottom: 32px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const HeaderSection = styled(Flex)`
  margin-bottom: 24px;
  padding-bottom: 20px;
`;

const NetworkLogo = styled.img`
  height: 32px;
  width: auto;
  object-fit: contain;
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CourseCard = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px ${toRgba(theme.colors.NEU_999, 0.12)};
    border-color: ${(props) => props.$brandColor};
  }
`;

const CourseThumbnail = styled.div`
  width: 100%;
  height: 140px;
  background: ${theme.colors.NEU_100};
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const PlaceholderIcon = styled.div`
  color: ${theme.colors.NEU_300};
`;

const RelevanceBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${(props) => props.$brandColor};
  color: ${theme.colors.NEU_0};
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
`;

const TagsContainer = styled(Flex)`
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

const Tag = styled.div`
  padding: 4px 10px;
  background: ${theme.colors.NEU_100};
  border-radius: 12px;
  font-size: 11px;
  color: ${theme.colors.NEU_600};
  font-weight: 500;
`;

const InstructorInfo = styled(Flex)`
  padding: 12px 0;
  border-top: 1px solid ${theme.colors.NEU_100};
  margin-top: 12px;
`;

const PriceTag = styled(Typo.MediumText)`
  color: ${(props) => props.$brandColor};
  font-weight: 700;
`;

const RatingContainer = styled(Flex)`
  gap: 4px;
`;

/**
 * CoursioNetworkRecommendations Component
 *
 * Displays course recommendations from the Coursio Network (list of clients).
 * Recommendations are relevant based on the student's current course list.
 *
 * Features:
 * - Shows courses from other instructors in the network
 * - Relevance scoring based on student's current courses
 * - Clean card-based layout
 * - Responsive design
 */
const CoursioNetworkRecommendations = ({ recommendations = [] }) => {
  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  // Sort by relevance score
  const sortedRecommendations = [...recommendations].sort((a, b) => b.relevanceScore - a.relevanceScore);

  return (
    <SectionContainer direction='column' align='flex-start'>
      <HeaderSection direction='column' align='flex-start' width='100%'>
        <Flex align='center' gap='12' style={{ marginBottom: '12px' }}>
          <NetworkLogo src={coursioNetworkLogo} alt='Coursio Network' />
        </Flex>
        <Typo.Text color={theme.colors.NEU_600} $textAlign='left'>
          Discover courses from our network of trusted instructors, tailored to complement your learning journey.
        </Typo.Text>
      </HeaderSection>

      <CoursesGrid>
        {sortedRecommendations.map((course) => (
          <CourseCard key={course.id} direction='column' align='flex-start' $brandColor={theme.colors.PRIMARY}>
            <CourseThumbnail>
              {course.thumbnailUrl ? (
                <img src={course.thumbnailUrl} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <PlaceholderIcon>
                  <Icon.Course size={'large'} color={theme.colors.NEU_300} />
                </PlaceholderIcon>
              )}
              <RelevanceBadge $brandColor={theme.colors.PRIMARY}>{course.relevanceScore}% Match</RelevanceBadge>
            </CourseThumbnail>

            <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY} $textAlign='left'>
              {course.title}
            </Typo.MediumText>

            <Typo.Info color={theme.colors.NEU_600} $textAlign='left' style={{ marginTop: '8px', lineHeight: '1.5' }}>
              {course.description}
            </Typo.Info>

            <Flex align='center' gap='8' style={{ marginTop: '12px' }}>
              <Icon.Info size={'x-small'} color={theme.colors.PRIMARY} />
              <Typo.Info color={theme.colors.PRIMARY} fontWeight='500'>
                {course.relevanceReason}
              </Typo.Info>
            </Flex>

            <TagsContainer>
              {course.tags.slice(0, 3).map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </TagsContainer>

            <InstructorInfo align='center' justify='space-between' width='100%'>
              <Flex direction='column' align='flex-start'>
                <Typo.Info color={theme.colors.NEU_600}>{course.instructor}</Typo.Info>
                <Typo.Info color={theme.colors.NEU_300} style={{ fontSize: '10px' }}>
                  {course.instructorStore}
                </Typo.Info>
              </Flex>

              <Flex direction='column' align='flex-end'>
                <RatingContainer align='center'>
                  <Icon.Certificate size={'x-small'} color={theme.colors.SECONDARY} />
                  <Typo.Info fontWeight='600' color={theme.colors.NEU_PRIMARY}>
                    {course.rating}
                  </Typo.Info>
                </RatingContainer>
                <Typo.Info color={theme.colors.NEU_600} style={{ fontSize: '10px' }}>
                  {course.studentsCount} students
                </Typo.Info>
              </Flex>
            </InstructorInfo>

            <Flex align='center' justify='space-between' width='100%' style={{ marginTop: '16px' }}>
              <PriceTag $brandColor={theme.colors.PRIMARY}>
                {course.price} {course.currency}
              </PriceTag>
              <Flex gap='8' direction='column'>
                <Flex gap='4'>
                  <Icon.Time size={'x-small'} color={theme.colors.NEU_600} />
                  <Typo.Info color={theme.colors.NEU_600}>{course.duration}</Typo.Info>
                </Flex>
                <Typo.Info color={theme.colors.NEU_600}>{course.level}</Typo.Info>
              </Flex>
            </Flex>

            <SimpleButton style={{ marginTop: '16px', width: '100%' }} onClick={() => console.log('View course:', course.id)}>
              View Course
            </SimpleButton>
          </CourseCard>
        ))}
      </CoursesGrid>

      <Flex justify='center' width='100%' style={{ marginTop: '24px' }}>
        <SimpleButton variant='outlined' endIcon='ExternalLink' onClick={() => window.open('https://coursio-web-2025.vercel.app/coursio-network/', '_blank')}>
          Explore More Courses
        </SimpleButton>
      </Flex>
    </SectionContainer>
  );
};

export default CoursioNetworkRecommendations;
